import type { LiveTrackerSound } from 'src/types/live-activity';

import { useRef, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { liveActivityApi } from 'src/api/live-activity.api';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export type CelebrationKind = 'order' | 'recharge';

/** Qué sonido suena: además de pedido/recarga normales, "big" es el aviso especial para un
 * pedido o recarga que supera el umbral de "venta grande" (Configurations en el backend). */
export type SoundKind = CelebrationKind | 'big';

export type Celebration = {
  /** Id del evento que dispara la celebración (sirve de clave para no repetirla). */
  id: number;
  /** Estilo visual (dorado/verde) y título — solo pedido o recarga, no cambia con "big". */
  kind: CelebrationKind;
  /** Qué sonido usar. Por defecto el mismo que `kind`; "big" si el importe lo supera. */
  soundKind?: SoundKind;
  title: string;
  subtitle?: string;
};

type Props = {
  celebration: Celebration | null;
  soundEnabled: boolean;
  /** Sonidos subidos por el administrador. Sin fichero para ese tipo, no suena nada. */
  soundByKind?: Record<string, LiveTrackerSound | undefined>;
};

const STYLE_BY_KIND: Record<CelebrationKind, { gradient: string; shadow: string; icon: string }> = {
  order: {
    gradient: 'linear-gradient(135deg, #FFD666 0%, #FFAB00 55%, #FF9800 100%)',
    shadow: '0 16px 40px -12px rgba(255, 171, 0, 0.7)',
    icon: 'solar:cup-star-bold',
  },
  recharge: {
    gradient: 'linear-gradient(135deg, #5BE49B 0%, #00A76F 55%, #007867 100%)',
    shadow: '0 16px 40px -12px rgba(0, 167, 111, 0.7)',
    icon: 'solar:wad-of-money-bold',
  },
};

const COLORS = ['#FFAB00', '#00A76F', '#00B8D9', '#FF5630', '#8E33FF', '#FFFFFF'];

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
};

// Los ficheros de sonido se descargan una sola vez por versión (clave: tipo + fecha de subida).
const audioCache = new Map<string, HTMLAudioElement>();

/**
 * Recompensa visual cuando entra un pedido o una recarga: confeti a pantalla completa + cartel
 * (dorado para pedidos, verde para recargas) y el sonido que haya subido el administrador.
 * El confeti se dibuja en un canvas propio, sin dependencias nuevas.
 */
export function LiveCelebration({ celebration, soundEnabled, soundByKind }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!celebration) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Particle[] = Array.from({ length: 140 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 6 + Math.random() * 9;
      return {
        x: canvas.width / 2,
        y: canvas.height * 0.32,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 4,
        size: 5 + Math.random() * 7,
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.3,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      };
    });

    const started = performance.now();
    const DURATION = 2600;

    const draw = (now: number) => {
      const elapsed = now - started;
      context.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.vy += 0.28; // gravedad
        p.vx *= 0.995;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;

        context.save();
        context.globalAlpha = Math.max(0, 1 - elapsed / DURATION);
        context.translate(p.x, p.y);
        context.rotate(p.rotation);
        context.fillStyle = p.color;
        context.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        context.restore();
      });

      if (elapsed < DURATION) {
        frameRef.current = requestAnimationFrame(draw);
      } else {
        context.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    frameRef.current = requestAnimationFrame(draw);

    if (soundEnabled) {
      const soundKind = celebration.soundKind ?? celebration.kind;
      void playCelebrationSound(soundKind, soundByKind?.[soundKind]);
    }

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      context.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [celebration, soundEnabled, soundByKind]);

  const style = STYLE_BY_KIND[celebration?.kind ?? 'order'];

  return (
    <>
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1500,
          pointerEvents: 'none',
        }}
      />

      <AnimatePresence>
        {celebration ? (
          <Box
            key={celebration.id}
            component={m.div}
            initial={{ opacity: 0, y: -40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            sx={{
              position: 'fixed',
              top: 96,
              left: '50%',
              translate: '-50% 0',
              zIndex: 1600,
              px: 3,
              py: 2,
              borderRadius: 2,
              color: '#1C252E',
              background: style.gradient,
              boxShadow: style.shadow,
              pointerEvents: 'none',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Iconify icon={style.icon as never} width={36} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {celebration.title}
                </Typography>
                {celebration.subtitle ? (
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {celebration.subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Box>
        ) : null}
      </AnimatePresence>
    </>
  );
}

/**
 * Reproduce el mp3/wav subido para ese aviso. Sin fichero subido para ese tipo (o si falla la
 * descarga/reproducción), no suena nada — el aviso queda solo visual. Antes se caía a un "ding"
 * sintetizado por defecto; se quita a propósito para que el único sonido posible sea el que el
 * administrador haya elegido explícitamente en Sonidos.
 */
export async function playCelebrationSound(
  kind: SoundKind,
  sound?: LiveTrackerSound
): Promise<void> {
  if (!sound) return;

  try {
    const cacheKey = `${kind}:${sound.updatedAt}`;
    let audio = audioCache.get(cacheKey);

    if (!audio) {
      const response = await liveActivityApi.getSoundFile(kind);
      audio = new Audio(URL.createObjectURL(response.data));
      audioCache.set(cacheKey, audio);
    }

    audio.currentTime = 0;
    await audio.play();
  } catch {
    // Fichero no disponible o el navegador bloquea el audio: se queda en silencio.
  }
}
